import { useFiatPricingContext, usePassportProvider } from "@/context";
import { truncateEthAddress } from "@/utils/tokens";
import { base } from "@biom3/design-tokens";
import { Body, ButtCon, Icon, Stack, Table, Tooltip } from "@biom3/react";
import { Avatar, AvatarGroup, Typography } from "@mui/material";
import { BigNumber } from "bignumber.js";
import { useMemo, useState } from "react";
import { Header } from "../TilesSection/Header";
import { useWeeklyRewards } from "./WeeklyRewardsProvider";
import type { GroupedMythicReward, MythicRewardResult } from "./types";

const formatSweepstakeSource = (source: string): string => {
  if (!source) return "Unknown";

  const generalPattern = /.*?-(\w+)-(\d+)/;
  if (source.match(generalPattern)) {
    const matches = source.match(generalPattern);
    if (matches && matches.length >= 3) {
      const word = matches[1];
      const number = matches[2];
      const capitalizedWord = word.charAt(0).toUpperCase() + word.slice(1);
      return `${capitalizedWord} ${number}`;
    }
  }

  return source;
};

export function JackpotTable({
  jackpotWinners,
}: {
  jackpotWinners: GroupedMythicReward[] | undefined;
}) {
  const { walletAddress } = usePassportProvider();
  const { getFormattedPrice } = useFiatPricingContext();
  const { prizeConfig } = useWeeklyRewards();

  const [currentJackpotIndex, setCurrentJackpotIndex] = useState<number>(0);

  const selectedJackpot = useMemo(() => {
    if (!jackpotWinners || jackpotWinners.length === 0) return undefined;
    return jackpotWinners[currentJackpotIndex];
  }, [jackpotWinners, currentJackpotIndex]);

  const handlePrevClick = () => {
    if (!jackpotWinners) return;
    setCurrentJackpotIndex((prev) =>
      prev < jackpotWinners.length - 1 ? prev + 1 : prev,
    );
  };

  const handleNextClick = () => {
    if (!jackpotWinners) return;
    setCurrentJackpotIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  if (!selectedJackpot || !jackpotWinners) return null;

  const canGoPrev = currentJackpotIndex < jackpotWinners.length - 1;
  const canGoNext = currentJackpotIndex > 0;

  const formattedQuestId = formatSweepstakeSource(
    selectedJackpot.questExternalID || "",
  );

  const formattedDate = new Date(
    selectedJackpot.startDate,
  ).toLocaleDateString();

  return (
    <>
      <Header
        showDivider={true}
        title="Mythic Reward Winners"
        testId="JackpotTable"
        action={
          jackpotWinners.length > 1 && (
            <Stack
              direction="row"
              gap="base.spacing.x8"
              sx={{ alignItems: "center", justifyContent: "center" }}
            >
              <ButtCon
                variant="tertiary"
                icon="ChevronBackward"
                onClick={handlePrevClick}
                disabled={!canGoPrev}
                aria-label="Previous draw"
              />
              <Stack
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0",
                }}
              >
                <Typography variant="subtitle1" sx={{ whiteSpace: "nowrap" }}>
                  {formattedQuestId}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "nowrap",
                    lineHeight: 1,
                  }}
                >
                  {formattedDate}
                </Typography>
              </Stack>
              <ButtCon
                variant="tertiary"
                icon="ChevronForward"
                onClick={handleNextClick}
                disabled={!canGoNext}
                aria-label="Next draw"
              />
            </Stack>
          )
        }
      />

      <Table
        rowHoverFx={false}
        sx={{
          margin: "0",
          mb: "base.spacing.x12",
          width: {
            default: "auto",
          },
        }}
      >
        <Table.Head
          sx={{
            borderRadius: "base.borderRadius.x10",
          }}
        >
          <Table.Row>
            <Table.Cell
              sx={{
                fontWeight: "bold",
                display: {
                  default: "none",
                  medium: "block",
                },
              }}
            >
              Mythic Rewards
            </Table.Cell>
            <Table.Cell sx={{ fontWeight: "bold" }}>Payout</Table.Cell>
            <Table.Cell sx={{ fontWeight: "bold" }}>Winners</Table.Cell>
          </Table.Row>
        </Table.Head>

        <Table.Body>
          {selectedJackpot.results.map((winnersGroup, index) => {
            const amount = winnersGroup[0]?.amount || "0";

            const rawAmount = new BigNumber(amount)
              .multipliedBy(10 ** 4)
              .toString();

            let numberOfWinners =
              prizeConfig.jackpots?.find((x) => x.imx_reward === amount)
                ?.winner_count || 0;

            if (numberOfWinners > 0) {
              numberOfWinners =
                numberOfWinners - Math.min(5, winnersGroup.length);
            }

            return (
              <Table.Row
                key={`amount-${amount}-${selectedJackpot.startDate}-${index}`}
              >
                <Table.Cell
                  sx={{
                    display: {
                      default: "none",
                      medium: "block",
                    },
                  }}
                >
                  <Body>
                    {index === 0
                      ? "1st"
                      : index === 1
                        ? "2nd"
                        : index === 2
                          ? "3rd"
                          : `${index + 1}th`}
                  </Body>
                </Table.Cell>
                <Table.Cell>
                  <Stack direction="row" gap="base.spacing.x2">
                    <Icon
                      variant="bold"
                      sx={{
                        fill: "base.gradient.1",
                        width: "base.icon.size.400",
                        height: "base.icon.size.400",
                        display: "flex",
                        alignSelf: "center",
                        verticalAlign: "middle",
                      }}
                      icon="ImxToken"
                    />
                    <Stack>
                      <Body sx={{ alignItems: "center", display: "flex" }}>
                        {new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 3,
                        }).format(Number(amount))}{" "}
                        IMX
                      </Body>
                      <Body
                        size="xSmall"
                        sx={{ color: "base.color.text.caption.secondary" }}
                      >
                        {getFormattedPrice("IMX", 4, rawAmount)}
                      </Body>
                    </Stack>
                  </Stack>
                </Table.Cell>
                <Table.Cell>
                  <Stack
                    direction="row"
                    justifyContent="flex-start"
                    sx={{
                      display: {
                        default: "none",
                        medium: "flex",
                      },
                    }}
                  >
                    <AvatarGroup
                      max={5}
                      total={winnersGroup.length}
                      sx={{
                        "& .MuiAvatar-root": {
                          width: 32,
                          height: 32,
                          fontSize: "0.875rem",
                          color: "inherit",
                        },
                      }}
                    >
                      {winnersGroup
                        .sort(
                          (a: MythicRewardResult, b: MythicRewardResult) => {
                            const truncatedWalletAddress = walletAddress
                              ? truncateEthAddress(walletAddress, 5, 5)
                              : "";

                            if (a.walletAddress === truncatedWalletAddress)
                              return -1;
                            if (b.walletAddress === truncatedWalletAddress)
                              return 1;

                            if (a.claimed && !b.claimed) return -1;
                            if (!a.claimed && b.claimed) return 1;

                            return 0;
                          },
                        )
                        .map((winner: MythicRewardResult) => {
                          const truncatedWalletAddress = walletAddress
                            ? truncateEthAddress(walletAddress)
                            : "";
                          const isWinner =
                            winner.walletAddress === truncatedWalletAddress;

                          return (
                            <Tooltip key={winner.walletAddress}>
                              <Tooltip.Target>
                                <Avatar
                                  sx={{
                                    background:
                                      winner.walletAddress ===
                                      truncatedWalletAddress
                                        ? base.color.text.heading.emphasis
                                        : winner.claimed
                                          ? base.color.translucent
                                              .standard[1000]
                                          : base.color.translucent
                                              .standard[400],
                                  }}
                                />
                              </Tooltip.Target>
                              <Tooltip.Content>
                                <Body
                                  size="medium"
                                  sx={{
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {winner.claimed
                                    ? truncateEthAddress(winner.walletAddress)
                                    : "Unclaimed"}
                                  {isWinner && " (you)"}
                                </Body>
                              </Tooltip.Content>
                            </Tooltip>
                          );
                        })}
                    </AvatarGroup>
                  </Stack>
                  <Stack
                    sx={{
                      display: {
                        default: "flex",
                        medium: "none",
                      },
                    }}
                  >
                    {winnersGroup.length}
                  </Stack>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
}
